import { useCallback, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useToast } from '@/components/ToastContainer'
import {
  Alert,
  Button,
  DatePicker,
  DateTimeRangePicker,
  Select,
  Spinner,
  Tabs,
} from '@/ui'
import { OverviewTab } from './attribution/OverviewTab'
import { ChannelFunnelTab } from './attribution/ChannelFunnelTab'
import { JourneyTab } from './attribution/JourneyTab'
import { AdsTab, type AdsTabActions } from './attribution/AdsTab'
import type { ChannelFunnelResult, PaymentAttributionData } from './attribution/types'
import styles from './PaymentAttribution.module.css'

// ============================================================
// 결제일 기준 유입경로 분석 — "이 기간에 결제한 사람이 언제, 어떤
// 채널로 유입됐나". 셸은 기간 필터/리포트/탭 URL 상태만 담당하고,
// 본문은 attribution/* 탭 컴포넌트로 분리한다.
// ============================================================

// ── 빠른검색 프리셋 (N일 전 00:00 ~ 지금 누적) ──
const PRESETS = [
  { key: '1d', label: '1일 전 (어제~오늘)', days: 1 },
  { key: '2d', label: '2일 전', days: 2 },
  { key: '3d', label: '3일 전', days: 3 },
  { key: '1w', label: '1주일 전', days: 7 },
  { key: '1m', label: '한 달 전', months: 1 },
  { key: 'month', label: '월별' },
] as const
type PresetKey = (typeof PRESETS)[number]['key']
const DEFAULT_PRESET: PresetKey = '1d'

type TabKey = 'ads' | 'overview' | 'funnel' | 'journey'
const TAB_ITEMS = [
  { key: 'ads', label: '광고 ROAS' },
  { key: 'overview', label: '개요' },
  { key: 'funnel', label: '채널 퍼널' },
  { key: 'journey', label: '결제 여정' },
]

// 날짜만 또는 datetime(YYYY-MM-DD[ T]HH:mm[:ss]) — 서버 스키마와 동일
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/
const MONTH_RE = /^\d{4}-\d{2}$/
// URL 컴팩트 datetime — 콜론 없는 YYYY-MM-DDTHHmmss (URLSearchParams가 :를 %3A로 인코딩하는 것 방지)
const URL_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{4,6}$/

/** 내부 datetime(콜론 포함) → URL 컴팩트 표기(콜론 제거) */
function toUrlDateTime(dt: string): string {
  return dt.replace(/:/g, '')
}

/** URL 컴팩트 표기(YYYY-MM-DDTHHmmss) → 내부 datetime(YYYY-MM-DDTHH:mm:ss) */
function fromUrlDateTime(v: string): string {
  const [date, time = ''] = v.split('T')
  const hh = time.slice(0, 2)
  const mm = time.slice(2, 4)
  const ss = time.slice(4, 6) || '00'
  return `${date}T${hh}:${mm}:${ss}`
}

const pad = (n: number) => String(n).padStart(2, '0')

/** 로컬(=KST 가정) 벽시계 datetime 문자열 — UTC 변환/Z 접미사 금지 */
function toDateTimeStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function toYm(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
}

function toDateOnly(v: string): string {
  return v.slice(0, 10)
}

/** N일 전 00:00 ~ 지금 */
function rangeForDaysAgo(days: number): { from: string; to: string } {
  const now = new Date()
  const start = new Date(now)
  start.setDate(now.getDate() - days)
  start.setHours(0, 0, 0, 0)
  return { from: toDateTimeStr(start), to: toDateTimeStr(now) }
}

/** N개월 전 00:00 ~ 지금 */
function rangeForMonthsAgo(months: number): { from: string; to: string } {
  const now = new Date()
  const start = new Date(now)
  start.setMonth(now.getMonth() - months)
  start.setHours(0, 0, 0, 0)
  return { from: toDateTimeStr(start), to: toDateTimeStr(now) }
}

/** 월별: 그 달 1일 00:00 ~ 말일 23:59:59 (현재월이면 지금까지) */
function monthRange(ym: string): { from: string; to: string } {
  const [ys, ms] = ym.split('-')
  const y = Number(ys)
  const mIdx = Number(ms) - 1
  const start = new Date(y, mIdx, 1, 0, 0, 0, 0)
  const now = new Date()
  const isCurrent = now.getFullYear() === y && now.getMonth() === mIdx
  const end = isCurrent ? now : new Date(y, mIdx + 1, 0, 23, 59, 59, 0)
  return { from: toDateTimeStr(start), to: toDateTimeStr(end) }
}

function presetRange(key: PresetKey, month: string): { from: string; to: string } {
  const p = PRESETS.find((x) => x.key === key)
  if (!p || key === 'month') return monthRange(month)
  if ('months' in p) return rangeForMonthsAgo(p.months)
  if ('days' in p) return rangeForDaysAgo(p.days)
  return rangeForDaysAgo(1)
}

interface ResolvedRange {
  from: string
  to: string
  /** Select 표시값 — 프리셋 키 또는 커스텀 범위일 때 'custom' */
  selectValue: PresetKey | 'custom'
  /** 월별 모드에서 선택된 연-월 (YYYY-MM) */
  month: string
}

function resolveRange(sp: URLSearchParams): ResolvedRange {
  const now = new Date()
  const fromRaw = sp.get('from')
  const toRaw = sp.get('to')
  // ① 커스텀 datetime 범위 우선 — URL 컴팩트 표기(콜론 없음) 우선, 레거시 콜론 표기도 허용
  const from = fromRaw && URL_DATETIME_RE.test(fromRaw) ? fromUrlDateTime(fromRaw) : fromRaw
  const to = toRaw && URL_DATETIME_RE.test(toRaw) ? fromUrlDateTime(toRaw) : toRaw
  if (from && to && DATETIME_RE.test(from) && DATETIME_RE.test(to)) {
    return { from, to, selectValue: 'custom', month: toYm(now) }
  }
  // ② 월별
  const monthParam = sp.get('month')
  if (monthParam && MONTH_RE.test(monthParam)) {
    return { ...monthRange(monthParam), selectValue: 'month', month: monthParam }
  }
  // ③ 프리셋 (없으면 기본)
  const presetParam = sp.get('preset')
  const key = (PRESETS.find((p) => p.key === presetParam)?.key ?? DEFAULT_PRESET) as PresetKey
  return { ...presetRange(key, toYm(now)), selectValue: key, month: toYm(now) }
}

function resolveTab(sp: URLSearchParams): TabKey {
  const tab = sp.get('tab')
  // 기본 진입 = 광고 ROAS (커머스 성과가 첫 화면)
  return tab === 'funnel' || tab === 'ads' || tab === 'journey' || tab === 'overview' ? tab : 'ads'
}

export function PaymentAttribution() {
  const [sp, setSp] = useSearchParams()
  const { from, to, selectValue, month } = useMemo(() => resolveRange(sp), [sp])
  const tab = resolveTab(sp)

  const attributionPath = tab === 'overview' || tab === 'journey'
    ? `/api/stats/payment-attribution?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    : null
  const attribution = useApiFetch<PaymentAttributionData>(attributionPath)

  // 채널 퍼널 API는 날짜 단위 스키마라 datetime 선택 시 날짜 부분만 전달한다.
  const funnelFrom = toDateOnly(from)
  const funnelTo = toDateOnly(to)
  const funnel = useApiFetch<ChannelFunnelResult>(
    tab === 'funnel'
      ? `/api/stats/channel-funnel?from=${encodeURIComponent(funnelFrom)}&to=${encodeURIComponent(funnelTo)}`
      : null,
  )

  // 개요 탭 ROAS KPI용 총 광고비 — ads 리포트에서 spend 총액만 재사용(날짜 단위 스키마).
  const overviewAds = useApiFetch<{ summary: { spend: { current: number } } }>(
    tab === 'overview'
      ? `/api/admin/dashboard/ads?from=${encodeURIComponent(funnelFrom)}&to=${encodeURIComponent(funnelTo)}`
      : null,
  )
  const overviewAdSpend = overviewAds.data?.summary.spend.current ?? null

  // 필터바 갱신 스피너 — 현재 탭이 실제로 fetch하는 소스만 검사한다.
  // (ads 탭은 AdsTab이 자체 스피너를 갖고, 셸은 관여하지 않는다)
  const refreshing = (() => {
    if (tab === 'overview') {
      return (
        (!attribution.error && (!attribution.data || attribution.data.from !== from || attribution.data.to !== to)) ||
        overviewAds.isLoading
      )
    }
    if (tab === 'journey') {
      return !attribution.error && (!attribution.data || attribution.data.from !== from || attribution.data.to !== to)
    }
    if (tab === 'funnel') {
      return !funnel.error && (!funnel.data || funnel.data.from !== funnelFrom || funnel.data.to !== funnelTo)
    }
    return false // ads 탭: 셸 스피너 미표시 (AdsTab 내부에서 처리)
  })()

  // Select 옵션 — 커스텀 범위 활성 시 '직접 지정' 항목을 앞에 노출
  const selectOptions = useMemo(() => {
    const base = PRESETS.map((p) => ({ value: p.key, label: p.label }))
    return selectValue === 'custom' ? [{ value: 'custom', label: '직접 지정' }, ...base] : base
  }, [selectValue])

  // 광고 ROAS 탭이 등록하는 액션(엑셀 업로드/광고비 입력) — 필터바 우측에 버튼으로 렌더.
  // 콜백 아이덴티티를 안정화해 AdsTab의 등록 effect가 매 렌더 재실행되지 않게 한다.
  const [adsActions, setAdsActions] = useState<AdsTabActions | null>(null)
  const handleRegisterAdsActions = useCallback((actions: AdsTabActions | null) => {
    setAdsActions(actions)
  }, [])

  const setTab = (key: string) => {
    const next = new URLSearchParams(sp)
    next.set('tab', key)
    setSp(next, { replace: true })
  }

  const applyPreset = (key: string) => {
    if (key === 'custom') return
    const next = new URLSearchParams(sp)
    next.delete('from')
    next.delete('to')
    if (key === 'month') {
      next.set('month', toYm(new Date()))
      next.delete('preset')
    } else {
      next.set('preset', key)
      next.delete('month')
    }
    setSp(next, { replace: true })
  }

  const applyMonth = (ym: string) => {
    const next = new URLSearchParams(sp)
    next.set('month', ym)
    next.delete('preset')
    next.delete('from')
    next.delete('to')
    setSp(next, { replace: true })
  }

  // DateTimeRangePicker 확정 — Date 쌍을 로컬(KST) 벽시계 문자열로 변환해 URL 커밋.
  const applyCustomRange = (range: { from: Date | null; to: Date | null }) => {
    if (!range.from || !range.to) return
    const next = new URLSearchParams(sp)
    // URL엔 콜론 없는 컴팩트 표기로 저장 (%3A 인코딩 방지)
    next.set('from', toUrlDateTime(toDateTimeStr(range.from)))
    next.set('to', toUrlDateTime(toDateTimeStr(range.to)))
    next.delete('preset')
    next.delete('month')
    setSp(next, { replace: true })
  }

  // ── 리포트 발급 (AI 해석 포함 — 수 초 소요) ──
  const { addToast } = useToast()
  const navigate = useNavigate()
  const { trigger: sendReport, isLoading: sendingReport } = useApiMutation<
    { sent: number; failed: number; recipients: number },
    { from: string; to: string }
  >('/api/stats/revenue-report/send')

  const reportRange = { from: toDateOnly(from), to: toDateOnly(to) }

  const openReport = () => {
    // httpOnly 쿠키 인증이라 새 탭 직접 열기로 충분 (HTML 응답)
    window.open(`/api/stats/revenue-report?from=${encodeURIComponent(reportRange.from)}&to=${encodeURIComponent(reportRange.to)}`, '_blank', 'noopener')
  }

  const handleSendReport = async () => {
    const result = await sendReport(reportRange)
    if (result) {
      addToast({
        type: result.failed > 0 ? 'error' : 'success',
        message: result.failed > 0
          ? `리포트 발송 일부 실패 — 성공 ${result.sent} / 실패 ${result.failed}`
          : `리포트 이메일 발송 완료 (${result.sent}명)`,
      })
    } else {
      addToast({ type: 'error', message: '리포트 발송에 실패했습니다 (수신자 설정 확인)' })
    }
  }

  return (
    <div className={styles.root}>
      {/* 기간 필터 */}
      <div className={styles.filterBar}>
        <div className={styles.customRange}>
          <Select
            size="sm"
            options={selectOptions}
            value={selectValue}
            onChange={(e) => applyPreset(e.target.value)}
            aria-label="빠른 기간 선택"
          />
          {selectValue === 'month' && (
            <DatePicker
              size="sm"
              mode="month"
              value={month}
              onChange={(_d, ym) => applyMonth(ym)}
            />
          )}
        </div>
        <div className={styles.customRange}>
          <DateTimeRangePicker
            size="sm"
            value={{ from, to }}
            onChange={applyCustomRange}
            placeholder="직접 기간 지정"
          />
          {refreshing && <Spinner size="sm" />}
        </div>
        <div className={styles.customRange}>
          <Button size="sm" variant="secondary" onClick={openReport}>
            리포트 보기
          </Button>
          <Button size="sm" variant="primary" loading={sendingReport} onClick={handleSendReport}>
            이메일 발송
          </Button>
          {/* 저장된 리포트 히스토리·PDF 다운로드는 전용 페이지에서 */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/commerce/revenue-reports?from=${reportRange.from}&to=${reportRange.to}`)}
          >
            매출 리포트 →
          </Button>
        </div>

        {/* 광고 ROAS 탭 전용 액션 — 필터바 우측에 붙여 한 줄로 노출 (AdsTab이 등록) */}
        {tab === 'ads' && adsActions && (
          <div className={styles.adsActions}>
            <Button size="sm" variant="secondary" onClick={adsActions.openUpload}>
              + 엑셀 업로드
            </Button>
            <Button size="sm" variant="primary" onClick={adsActions.openInput}>
              + 광고비 입력
            </Button>
          </div>
        )}
      </div>

      {(tab === 'overview' || tab === 'journey') && attribution.error && (
        <Alert variant="danger">{attribution.error}</Alert>
      )}

      <Tabs value={tab} onChange={setTab} items={TAB_ITEMS} />

      {tab === 'overview' && (
        <OverviewTab data={attribution.data} error={attribution.error} adSpend={overviewAdSpend} />
      )}
      {tab === 'funnel' && (
        <ChannelFunnelTab
          data={funnel.data}
          error={funnel.error}
          isLoading={funnel.isLoading}
        />
      )}
      {tab === 'ads' && (
        <AdsTab from={funnelFrom} to={funnelTo} onRegisterActions={handleRegisterAdsActions} />
      )}
      {tab === 'journey' && (
        <JourneyTab
          data={attribution.data}
          error={attribution.error}
        />
      )}
    </div>
  )
}
